/**
 * @file        src/lib/seoRoutes.js
 * @description ES-module MIRROR of booking/booking_backend/utils/seoRoutes.js,
 *              the SEO route registry (which bucket every route is in). The
 *              backend resolves the real tags; this copy exists so the build
 *              can fail SAFE when the SEO API is unreachable (fallbackRobots)
 *              and so `npm run selftest:seo` can check every <Route> in App.jsx
 *              is in a bucket. BYTE-IDENTICAL in the neon and beyond builds,
 *              and its SEO_ROUTES table must equal the backend copy -- the
 *              self-test fails on either drift.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-28 Claude — Dropped the tripPlanner (/trip-planner) entry: Beyond's
 *                          page is removed, as Neon's was on 2026-08-03, so no
 *                          storefront serves it any more.
 *   - 2026-10-13 Claude — /amendment/* (the Full Refund page) registered as private (noindex, nofollow).
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */

export const SEO_ROUTES = [
  // ── index: INDEX, FOLLOW ────────────────────────────────────────────────
  { key: "home", pattern: "/", bucket: "index", label: "Home", priority: 1.0, changefreq: "daily" },
  { key: "packages.list", pattern: "/packages", bucket: "index", label: "Holiday Packages", priority: 0.9, changefreq: "daily" },
  { key: "packages.detail", pattern: "/packages/:idOrSlug", bucket: "index", label: "Holiday Package", item: "package", priority: 0.8, changefreq: "weekly" },
  { key: "blog.list", pattern: "/blog", bucket: "index", label: "Travel Blog", priority: 0.7, changefreq: "daily" },
  { key: "blog.detail", pattern: "/blog/:slug", bucket: "index", label: "Blog Post", item: "blog", priority: 0.6, changefreq: "monthly" },
  { key: "offers.list", pattern: "/offers", bucket: "index", label: "Exclusive Offers", priority: 0.7, changefreq: "daily", optional: true },
  { key: "about", pattern: "/about", bucket: "index", label: "About Us", priority: 0.5, changefreq: "monthly" },
  { key: "faqs", pattern: "/faqs", bucket: "index", label: "FAQs", priority: 0.5, changefreq: "monthly" },
  { key: "testimonial", pattern: "/testimonial", bucket: "index", label: "Testimonials", priority: 0.4, changefreq: "monthly" },
  { key: "contact", pattern: "/contact", bucket: "index", label: "Contact Us", priority: 0.5, changefreq: "yearly" },
  { key: "insurance", pattern: "/insurance", bucket: "index", label: "Travel Insurance", priority: 0.7, changefreq: "weekly" },
  { key: "legal.terms", pattern: "/legal/terms", bucket: "index", label: "Terms & Conditions", priority: 0.2, changefreq: "yearly" },
  { key: "legal.privacy", pattern: "/legal/privacy", bucket: "index", label: "Privacy Policy", priority: 0.2, changefreq: "yearly" },

  // ── search: NOINDEX, FOLLOW ─────────────────────────────────────────────
  { key: "flights.results", pattern: "/flights/results", bucket: "search", label: "Flight Results" },
  { key: "hotels.results", pattern: "/hotels/results", bucket: "search", label: "Hotel Results" },
  { key: "hotels.detail", pattern: "/hotels/detail", bucket: "search", label: "Hotel Details" },
  { key: "insurance.results", pattern: "/insurance/results", bucket: "search", label: "Insurance Results" },

  // ── private: NOINDEX, NOFOLLOW ──────────────────────────────────────────
  { key: "flights.book", pattern: "/flights/book", bucket: "private", label: "Flight Booking" },
  { key: "flights.review", pattern: "/flights/review", bucket: "private", label: "Flight Review" },
  { key: "flights.payment", pattern: "/flights/payment", bucket: "private", label: "Flight Payment" },
  { key: "flights.modify", pattern: "/flights/modify", bucket: "private", label: "Modify Booking" },
  { key: "flights.reschedule", pattern: "/flights/reschedule", bucket: "private", label: "Reschedule" },
  { key: "hotels.checkout", pattern: "/hotels/checkout", bucket: "private", label: "Hotel Checkout" },
  { key: "hotels.payment", pattern: "/hotels/payment", bucket: "private", label: "Hotel Payment" },
  { key: "hotels.hold", pattern: "/hotels/hold/*", bucket: "private", label: "Hotel Hold" },
  { key: "insurance.book", pattern: "/insurance/book", bucket: "private", label: "Insurance Booking" },
  { key: "confirmation", pattern: "/confirmation", bucket: "private", label: "Confirmation" },
  { key: "voucher", pattern: "/voucher/*", bucket: "private", label: "Voucher" },
  { key: "booking", pattern: "/booking/*", bucket: "private", label: "Booking Details" },
  { key: "hotelBookingDetails", pattern: "/hotel-booking-details/*", bucket: "private", label: "Stay Details" },
  { key: "cartDetail", pattern: "/cart-detail/*", bucket: "private", label: "Cart Details" },
  { key: "amendment", pattern: "/amendment/*", bucket: "private", label: "Amendment" },
  { key: "invoice", pattern: "/invoice/*", bucket: "private", label: "Invoice" },
  { key: "profile", pattern: "/profile", bucket: "private", label: "Profile" },
  { key: "wallet", pattern: "/wallet", bucket: "private", label: "Wallet" },
];

export const BUCKETS = ["index", "search", "private"];

/** Path without query/hash, with a leading slash and no trailing slash (root excepted). */
export function normalizePath(raw) {
  let p = String(raw == null ? "" : raw).trim();
  const cut = p.search(/[?#]/);
  if (cut >= 0) p = p.slice(0, cut);
  if (!p.startsWith("/")) p = "/" + p;
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

function matchPattern(pattern, path) {
  const want = pattern.split("/").filter(Boolean);
  const got = path.split("/").filter(Boolean);
  const params = {};
  for (let i = 0; i < want.length; i++) {
    const w = want[i];
    if (w === "*") return got.length > i ? params : null;
    if (i >= got.length) return null;
    if (w.startsWith(":")) {
      let v = got[i];
      try { v = decodeURIComponent(v); } catch (_) { /* keep raw */ }
      params[w.slice(1)] = v;
      continue;
    }
    if (w.toLowerCase() !== got[i].toLowerCase()) return null;
  }
  return got.length === want.length ? params : null;
}

/**
 * The registry entry a path belongs to, or null for a path no route serves.
 * Static patterns are tried before parameterised ones so "/packages" can never
 * be swallowed by "/packages/:idOrSlug".
 */
export function matchRoute(rawPath) {
  const path = normalizePath(rawPath);
  const ordered = SEO_ROUTES.slice().sort((a, b) => specificity(b) - specificity(a));
  for (const entry of ordered) {
    const params = matchPattern(entry.pattern, path);
    if (params) return { entry, params, path };
  }
  return null;
}

function specificity(e) {
  return e.pattern.split("/").filter(Boolean)
    .reduce((n, s) => n + (s === "*" ? 1 : s.startsWith(":") ? 2 : 3), 0);
}

export function entryByKey(key) {
  return SEO_ROUTES.find((e) => e.key === key) || null;
}

/** The pages an admin edits on Page SEO: the index bucket only. */
export function indexableEntries() {
  return SEO_ROUTES.filter((e) => e.bucket === "index");
}


/**
 * The robots value this build applies on its OWN when the SEO API cannot be
 * reached. Only ever SUBTRACTS: a search or private route is never left on the
 * index-follow default that index.html ships with, so an API outage can never
 * expose a voucher or an invoice to a crawler. null = keep the static default.
 */
export function fallbackRobots(path) {
  const m = matchRoute(path);
  if (!m) return "noindex, nofollow";
  if (m.entry.bucket === "private") return "noindex, nofollow";
  if (m.entry.bucket === "search") return "noindex, follow";
  return null;
}
